import React, { useState } from 'react';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useFirestore, useFirestoreConnect } from 'react-redux-firebase';
import { useParams } from 'react-router-dom';
import type { AppState } from '../config/store';
import {
  resolveSurveyFormConfig,
  FORM_CONFIG_DOC_ID,
} from '../state/form-config';
import {
  FLOOR_PLANS_CONFIG_DOC_ID,
  resolveSurveyFloorPlansConfig,
} from '../state/floorplans-config';
import { EMPTY_OBJECT } from '../shared/helpers';
import type { SurveyProps } from '../types/survey';
import { UrlParams } from '../types/urlParams';

function useSurvey() {
  const { clientId, surveyId } = useParams<UrlParams>();

  const survey = useSelector(
    (state: AppState) => state.firestore.data.survey || EMPTY_OBJECT,
  );
  const surveyFormConfig = useSelector(
    (state: AppState) => state.firestore.data.surveyFormConfig || EMPTY_OBJECT,
  );
  const surveyFloorPlansConfig = useSelector(
    (state: AppState) =>
      state.firestore.data.surveyFloorPlansConfig || EMPTY_OBJECT,
  );
  useFirestoreConnect([
    {
      collection: 'clients',
      doc: clientId,
      subcollections: [{ collection: 'surveys', doc: surveyId }],
      storeAs: 'survey',
    },
    {
      collection: 'clients',
      doc: clientId,
      subcollections: [
        { collection: 'surveys', doc: surveyId },
        { collection: 'config', doc: FORM_CONFIG_DOC_ID },
      ],
      storeAs: 'surveyFormConfig',
    },
    {
      collection: 'clients',
      doc: clientId,
      subcollections: [
        { collection: 'surveys', doc: surveyId },
        { collection: 'config', doc: FLOOR_PLANS_CONFIG_DOC_ID },
      ],
      storeAs: 'surveyFloorPlansConfig',
    },
  ]);

  const resolvedFormConfig = useMemo(
    () =>
      resolveSurveyFormConfig({
        surveyDocData: survey as SurveyProps,
        formConfigData: surveyFormConfig,
        context: 'useSurvey',
        surveyId,
      }),
    [survey, surveyFormConfig, surveyId],
  );
  const resolvedFloorPlansConfig = useMemo(
    () =>
      resolveSurveyFloorPlansConfig({
        surveyDocData: survey as SurveyProps,
        floorPlansConfigData: surveyFloorPlansConfig,
        context: 'useSurvey',
        surveyId,
      }),
    [survey, surveyFloorPlansConfig, surveyId],
  );
  const { formFields, formOptions } = resolvedFormConfig;
  const { floorPlans } = resolvedFloorPlansConfig;

  return useMemo(() => {
    return { ...survey, formFields, formOptions, floorPlans };
  }, [survey, formFields, formOptions, floorPlans]);
}
export function useGetSurvey() {
  const { clientId, surveyId } = useParams<UrlParams>();

  const [survey, setSurvey] = useState<SurveyProps>({} as SurveyProps);

  const firestore = useFirestore();

  React.useEffect(() => {
    async function getSurvey() {
      const docSnap = await firestore
        .collection('clients')
        .doc(clientId)
        .collection('surveys')
        .doc(surveyId)
        .get();
      if (docSnap.exists) {
        const surveyDocData = docSnap.data() as SurveyProps;
        const formConfigSnap = await firestore
          .collection('clients')
          .doc(clientId)
          .collection('surveys')
          .doc(surveyId)
          .collection('config')
          .doc(FORM_CONFIG_DOC_ID)
          .get();
        const floorPlansConfigSnap = await firestore
          .collection('clients')
          .doc(clientId)
          .collection('surveys')
          .doc(surveyId)
          .collection('config')
          .doc(FLOOR_PLANS_CONFIG_DOC_ID)
          .get();
        const formConfigData = formConfigSnap.exists
          ? formConfigSnap.data()
          : {};
        const floorPlansConfigData = floorPlansConfigSnap.exists
          ? floorPlansConfigSnap.data()
          : {};
        const { formFields, formOptions } = resolveSurveyFormConfig({
          surveyDocData,
          formConfigData,
          context: 'useGetSurvey',
          surveyId,
        });
        const { floorPlans } = resolveSurveyFloorPlansConfig({
          surveyDocData,
          floorPlansConfigData,
          context: 'useGetSurvey',
          surveyId,
        });
        setSurvey({
          ...surveyDocData,
          formFields,
          formOptions,
          floorPlans,
        } as SurveyProps);
      }
    }

    if (clientId && surveyId) {
      getSurvey();
    }
  }, [clientId, surveyId]);

  return survey;
}

export default useSurvey;
